import {Accordion, Button, Checkbox} from '@mantine/core';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {SegmentedControl} from 'src/design/atoms/SegmentedControl';
import {Text} from 'src/design/atoms/Text';
import fileDownload from 'js-file-download';
import React, {useMemo, useState} from 'react';
import type {
  ModuleEnum,
  TaskAssignmentBatchStatusByTaskUpdateRequest,
  TaskAssignmentSimpleResponse,
  TaskDefinitionResponse,
} from 'src/api/ProactApiClient';
import {
  NoteModuleEnum,
  PermissionOperationEnum,
  TaskApprovalFlowTypeEnum,
  TaskAssignmentStateEnum,
  TaskCompletionTypeEnum,
  TaskDefinitionStateEnum,
} from 'src/api/ProactApiClient';
import {
  useGetTaskAssignmentsListQuery,
  usePostBatchStatusUpdateByTaskMutation,
} from 'src/redux/queries/taskAssignment';
import {useExportTaskStatusReportMutation} from 'src/redux/queries/taskDefinition';
import {ExportButton} from 'src/design/molecules/ExportButton';
import {Pill} from 'src/design/atoms/Pill';
import {PermissionGuard} from 'src/app/security/authorization/PermissionGuard';
import {Textbox} from 'src/design/atoms/Textbox';
import {CheckboxRow} from 'src/design/molecules/CheckboxRow';
import {NotesModule} from 'src/app/notes/NotesModule';
import {ConfirmActionButton} from 'src/design/molecules/ConfirmActionButton';
import {
  bulkTaskActionBody,
  TaskAssignmentPillConfig,
  TASK_ASSIGNMENT_OPEN_STATES,
  transitionAppliesToTaskType,
} from 'src/app/tasks/taskHelpers';
import {Paginator} from 'src/design/organisms/Pagination';
import {useListState} from 'src/design/organisms/useListState';
import {TableBodyRender} from 'src/design/molecules/TableBodyRender';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {useTaskPermissions} from 'src/shared/auth/permissions/useTaskPermissions';
import {useAlert} from 'src/shared/hooks/useAlert';
import useDebounce from 'src/shared/hooks/useDebounce';
import {useStateMachine} from 'src/shared/hooks/api/useStateMachine';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {TaskParticipationBanner} from './TaskParticipationBanner';
import {TaskActiveView} from './taskInterfaces';

const viewsForApprovalFlow = (
  approvalFlowType: TaskApprovalFlowTypeEnum
): TaskActiveView[] =>
  approvalFlowType === TaskApprovalFlowTypeEnum.Required
    ? [
        TaskActiveView.Incomplete,
        TaskActiveView.ReadyForReview,
        TaskActiveView.Complete,
      ]
    : [TaskActiveView.Incomplete, TaskActiveView.Complete];

const statesForView = (view: TaskActiveView): TaskAssignmentStateEnum[] => {
  switch (view) {
    case TaskActiveView.Complete:
      return [
        TaskAssignmentStateEnum.Completed,
        TaskAssignmentStateEnum.Cancelled,
        TaskAssignmentStateEnum.Rejected,
      ];
    case TaskActiveView.ReadyForReview:
      return [TaskAssignmentStateEnum.Submitted];
    case TaskActiveView.Incomplete:
      return TASK_ASSIGNMENT_OPEN_STATES;
    default:
      return [];
  }
};

export interface AdminTaskAssignmentsListByTaskProps {
  item: TaskDefinitionResponse;
  onTransition: () => Promise<void>;
  module: ModuleEnum;
}

export const AdminTaskAssignmentsListByTask: React.FC<
  AdminTaskAssignmentsListByTaskProps
> = ({item, onTransition, module}) => {
  const maxPageRows = 20;
  const {setAlert} = useAlert();

  const isMutuallyExclusiveAssignment =
    item.completionType === TaskCompletionTypeEnum.MutuallyExclusive;

  const taskPerms = useTaskPermissions(module);
  const [assignmentStateMachine] = useStateMachine({
    url: ApiEndpoints.getTaskAssignmentStateMachine,
  });

  const [exportTaskStatusReport] = useExportTaskStatusReportMutation();
  const [postBatchStatusUpdateByTask, {isLoading: isSaving}] =
    usePostBatchStatusUpdateByTaskMutation();
  const [hasSelectedAll, setHasSelectedAll] = useState<boolean>(false);
  const [selected, setSelected] = useState<number[]>([]);
  const {
    searchTerm: search,
    setSearchTerm: setSearch,
    filters,
    setFilters,
    currentPage,
    setCurrentPage,
    start,
    length,
  } = useListState({
    identifier: `admin-task-assignments-by-task-${item.id}`,
    pageSize: maxPageRows,
    defaultFilters: {activeView: TaskActiveView.Incomplete},
  });
  const debouncedSearch = useDebounce(search, 500);

  const stats = item.participation;
  const views = useMemo(
    () => viewsForApprovalFlow(item.approvalFlowType),
    [item.approvalFlowType]
  );
  const taskActiveView = views.includes(filters.activeView)
    ? filters.activeView
    : TaskActiveView.Incomplete;
  const states = useMemo(() => statesForView(taskActiveView), [taskActiveView]);

  const {data: listResponse, isLoading} = useGetTaskAssignmentsListQuery({
    taskId: item.id,
    assignmentStates: states,
    start,
    length,
    search: debouncedSearch,
  });

  const displayData: TaskAssignmentSimpleResponse[] = listResponse?.data ?? [];
  const totalRecords = listResponse?.totalRecords ?? 0;

  const submitBulkTransition = async (transition: string) => {
    const selectedIDs = selected.map(id => id);

    try {
      const request: TaskAssignmentBatchStatusByTaskUpdateRequest = {
        taskId: item.id,
        transition: transition,
        selectedAll: hasSelectedAll,
        taskAssignmentIds: selectedIDs,
      };

      await postBatchStatusUpdateByTask(request).unwrap();

      setAlert(`Selected items successfully updated.`, 'success');

      setSelected([]);
      setHasSelectedAll(false);
      void onTransition();
    } catch (error: unknown) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    e.stopPropagation();

    const {checked} = e.target;

    if (checked) {
      setSelected(prevState => [...prevState, id]);
    } else {
      setSelected(prevState => prevState.filter(i => i != id));
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {checked} = e.target;

    setHasSelectedAll(checked);

    if (checked === false) {
      setSelected([]);
    }
  };

  const handleMarkAsCancelled = async () => {
    await submitBulkTransition('cancel');
  };
  const handleMarkAsCompleted = async () => {
    await submitBulkTransition('complete');
  };
  const handleMarkAsApprovalApproved = async () => {
    await submitBulkTransition('approval-approve');
  };
  const handleMarkAsApprovalRejected = async () => {
    await submitBulkTransition('approval-reject');
  };
  const handleMarkAsApproved = async () => {
    await submitBulkTransition('approve');
  };
  const handleMarkAsReturned = async () => {
    await submitBulkTransition('return-for-rework');
  };

  const handleOptionKeyClick = (value: TaskActiveView) => {
    setSelected([]);
    setHasSelectedAll(false);
    setFilters({activeView: value});
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setSelected([]);
    setHasSelectedAll(false);
    setCurrentPage(page);
  };

  const handleExport = async () => {
    try {
      const response = await exportTaskStatusReport(item.id).unwrap();
      fileDownload(
        response?.data as Blob,
        `export_attendance_${item.id}-${new Date().getTime()}.csv`
      );
    } catch (error: any) {
      setAlert(
        parseErrorMessage(error) || 'error downloading the file',
        'error'
      );
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {value} = e.target;
    setSearch(value);
  };

  const selectedDisplayCount = hasSelectedAll ? totalRecords : selected.length;
  const isDraft = item.state === TaskDefinitionStateEnum.Draft;

  return (
    <Box>
      {!isDraft && <TaskParticipationBanner item={stats} />}
      <Group justify="space-between" align="flex-start">
        <SegmentedControl
          value={String(taskActiveView)}
          onChange={val => handleOptionKeyClick(Number(val) as TaskActiveView)}
          aria-label="Assignment Status"
          data={views.map(view => ({
            value: String(view),
            label:
              view === TaskActiveView.Incomplete
                ? `Incomplete (${stats?.incomplete})`
                : view === TaskActiveView.Complete
                ? `Complete (${stats?.completed})`
                : `Ready for Review (${stats?.pendingReview})`,
            disabled:
              (hasSelectedAll && taskActiveView !== view) ||
              (search?.length > 0 && taskActiveView !== view),
          }))}
        />
        <Group>
          <PermissionGuard
            module={module}
            operation={PermissionOperationEnum.Update}>
            {isMutuallyExclusiveAssignment ? (
              <Text c="dimmed" size="xs">
                Bulk actions are disabled when task completion type is{' '}
                <b>Assigned to many for one.</b> Use the primary actions on the
                main tab to modify this task.
              </Text>
            ) : (
              <>
                {taskActiveView === TaskActiveView.ReadyForReview && (
                  <>
                    <ConfirmActionButton
                      body={bulkTaskActionBody('approve', selectedDisplayCount)}
                      onConfirm={handleMarkAsApproved}
                      loading={isSaving}
                      renderButton={(onClick, loading) => (
                        <Button
                          color="green"
                          mr="md"
                          loading={loading}
                          disabled={
                            selectedDisplayCount === 0 ||
                            taskActiveView !== TaskActiveView.ReadyForReview ||
                            isDraft
                          }
                          onClick={
                            onClick
                          }>{`Mark ${selectedDisplayCount} as Approved`}</Button>
                      )}
                    />
                    <ConfirmActionButton
                      body={bulkTaskActionBody(
                        'return-for-rework',
                        selectedDisplayCount
                      )}
                      onConfirm={handleMarkAsReturned}
                      loading={isSaving}
                      renderButton={(onClick, loading) => (
                        <Button
                          color="red"
                          mr="md"
                          loading={loading}
                          disabled={
                            selectedDisplayCount === 0 ||
                            taskActiveView !== TaskActiveView.ReadyForReview ||
                            isDraft
                          }
                          onClick={
                            onClick
                          }>{`Mark ${selectedDisplayCount} as Returned`}</Button>
                      )}
                    />
                  </>
                )}
                {taskActiveView === TaskActiveView.Incomplete &&
                  transitionAppliesToTaskType(
                    assignmentStateMachine,
                    'complete',
                    item.taskType
                  ) && (
                    <ConfirmActionButton
                      body={bulkTaskActionBody(
                        'complete',
                        selectedDisplayCount
                      )}
                      onConfirm={handleMarkAsCompleted}
                      loading={isSaving}
                      renderButton={(onClick, loading) => (
                        <Button
                          mr="md"
                          loading={loading}
                          disabled={selectedDisplayCount === 0 || isDraft}
                          onClick={
                            onClick
                          }>{`Mark ${selectedDisplayCount} as Completed`}</Button>
                      )}
                    />
                  )}
                {taskActiveView === TaskActiveView.Incomplete &&
                  transitionAppliesToTaskType(
                    assignmentStateMachine,
                    'approval-approve',
                    item.taskType
                  ) && (
                    <ConfirmActionButton
                      body={bulkTaskActionBody(
                        'approval-approve',
                        selectedDisplayCount
                      )}
                      onConfirm={handleMarkAsApprovalApproved}
                      loading={isSaving}
                      renderButton={(onClick, loading) => (
                        <Button
                          mr="md"
                          loading={loading}
                          disabled={selectedDisplayCount === 0 || isDraft}
                          onClick={
                            onClick
                          }>{`Mark ${selectedDisplayCount} as Approved`}</Button>
                      )}
                    />
                  )}{' '}
                {taskActiveView === TaskActiveView.Incomplete &&
                  transitionAppliesToTaskType(
                    assignmentStateMachine,
                    'approval-reject',
                    item.taskType
                  ) && (
                    <ConfirmActionButton
                      body={bulkTaskActionBody(
                        'approval-reject',
                        selectedDisplayCount
                      )}
                      onConfirm={handleMarkAsApprovalRejected}
                      loading={isSaving}
                      renderButton={(onClick, loading) => (
                        <Button
                          mr="md"
                          loading={loading}
                          disabled={selectedDisplayCount === 0 || isDraft}
                          onClick={
                            onClick
                          }>{`Mark ${selectedDisplayCount} as Rejected`}</Button>
                      )}
                    />
                  )}
                {taskActiveView !== TaskActiveView.Complete && (
                  <ConfirmActionButton
                    body={bulkTaskActionBody('cancel', selectedDisplayCount)}
                    onConfirm={handleMarkAsCancelled}
                    loading={isSaving}
                    renderButton={(onClick, loading) => (
                      <Button
                        mr="md"
                        loading={loading}
                        disabled={selectedDisplayCount === 0 || isDraft}
                        onClick={
                          onClick
                        }>{`Mark ${selectedDisplayCount} as Cancelled`}</Button>
                    )}
                  />
                )}
              </>
            )}
          </PermissionGuard>
          <ExportButton onClick={handleExport} disabled={isDraft} />
        </Group>
      </Group>
      <Group justify="space-between" align="flex-start" mt="lg">
        <PermissionGuard
          module={module}
          operation={PermissionOperationEnum.Update}>
          <CheckboxRow
            label={`select all (${selectedDisplayCount} selected)`}
            disabled={
              taskActiveView === TaskActiveView.Complete ||
              isSaving ||
              isDraft ||
              isMutuallyExclusiveAssignment
            }
            checked={hasSelectedAll}
            onChange={handleSelectAll}
          />
        </PermissionGuard>
        <Textbox
          label="Search"
          ariaLabel="searchIndex"
          name="search"
          value={search}
          onChangeCallback={handleSearch}
        />
      </Group>
      {isLoading === false ? (
        <>
          <Paginator
            mt={16}
            mb={16}
            currentPage={currentPage}
            totalRecords={totalRecords}
            perPage={maxPageRows}
            showRecordCount={false}
            onChange={handlePageChange}
          />
          {displayData.length > 0 ? (
            <Accordion>
              {displayData.map(item => {
                const isCompleted =
                  item.state === TaskAssignmentStateEnum.Completed ||
                  item.state === TaskAssignmentStateEnum.Cancelled ||
                  item.state === TaskAssignmentStateEnum.Rejected;
                return (
                  <Accordion.Item key={item.id} value={String(item.id)}>
                    <Center>
                      {!isCompleted && (
                        <Checkbox
                          id={item.id.toString()}
                          ml={8}
                          disabled={
                            !taskPerms.update ||
                            isSaving ||
                            isDraft ||
                            isMutuallyExclusiveAssignment
                          }
                          checked={selected.includes(item.id) || hasSelectedAll}
                          onChange={e => handleSelect(e, item.id)}
                        />
                      )}
                      <Accordion.Control>
                        <Group>
                          <Text size="sm" style={{minWidth: 170}}>
                            {item.assignedToUser.name}
                          </Text>
                          <Box ml="lg">
                            {TaskAssignmentPillConfig(
                              item.state,
                              item.taskDefinition.taskType,
                              item.taskDefinition.state,
                              item.taskDefinition.dueAt
                            )?.map(pill => {
                              return <Pill key={pill.text} {...pill} />;
                            })}
                          </Box>
                        </Group>
                      </Accordion.Control>
                    </Center>
                    <Accordion.Panel>
                      <NotesModule
                        entityId={item.id}
                        noteType={NoteModuleEnum.TaskAssignment}
                      />
                    </Accordion.Panel>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          ) : (
            <Box pt="xl">No selections found</Box>
          )}
        </>
      ) : (
        <TableBodyRender isLoading={isLoading} />
      )}
    </Box>
  );
};
